import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/** @type {WeakMap<Element, { target: AccordionCustom }>} */
const pendingSingleOpenOperations = new WeakMap();

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    this.details.addEventListener('click', this.#handleContentClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    const group = this.closest('.accordion');
    const singleOpenGroup = group?.hasAttribute('data-single-open') ? group : null;
    const pendingOperation = singleOpenGroup ? pendingSingleOpenOperations.get(singleOpenGroup) : null;
    if (pendingOperation) {
      event.preventDefault();
      pendingOperation.target = this;
      return;
    }

    if (this.details.hasAttribute('data-animating-close')) {
      event.preventDefault();
      return;
    }

    if (this.details.open) {
      event.preventDefault();
      void this.#closeWithAnimation(this);
      return;
    }

    if (!singleOpenGroup) return;

    const openSiblings = this.#getGroupItems(singleOpenGroup).filter((item) => item !== this && item.details.open);
    if (openSiblings.length === 0) return;

    event.preventDefault();

    const operation = { target: this };
    pendingSingleOpenOperations.set(singleOpenGroup, operation);

    Promise.all(openSiblings.map((item) => this.#closeWithAnimation(item))).then(() => {
      if (pendingSingleOpenOperations.get(singleOpenGroup) !== operation) return;

      pendingSingleOpenOperations.delete(singleOpenGroup);

      if (operation.target.isConnected) operation.target.details.open = true;
    });
  };

  /**
   * Returns the accordion rows that belong directly to this single-open group.
   *
   * @param {Element} group - The parent accordion block.
   * @returns {AccordionCustom[]}
   */
  #getGroupItems(group) {
    /** @type {AccordionCustom[]} */
    const items = Array.from(group.querySelectorAll('accordion-custom'));

    return items.filter((item) => item.closest('.accordion') === group);
  }

  /**
   * Animates an accordion row closed before changing its native open state.
   *
   * @param {AccordionCustom} item - The accordion row to close.
   * @returns {Promise<void>}
   */
  async #closeWithAnimation(item) {
    const content = item.querySelector('.details-content');
    if (!content || !item.details.open) {
      item.details.open = false;
      return;
    }

    item.details.setAttribute('data-animate-close', '');
    const duration = this.#getTransitionDuration(content);
    if (!Number.isFinite(duration) || duration <= 0) {
      item.details.open = false;
      item.details.removeAttribute('data-animate-close');
      return;
    }

    const contentStyle = window.getComputedStyle(content);
    const easing = window
      .getComputedStyle(item.details)
      .getPropertyValue('--accordion-transition-easing')
      .trim();
    const closeAnimation = content.animate(
      [
        {
          blockSize: `${content.getBoundingClientRect().height}px`,
          opacity: contentStyle.opacity,
          paddingBlockStart: contentStyle.paddingBlockStart,
          paddingBlockEnd: contentStyle.paddingBlockEnd,
        },
        { blockSize: '0px', opacity: 0, paddingBlockStart: '0px', paddingBlockEnd: '0px' },
      ],
      { duration, easing: easing || 'ease-in-out', fill: 'both' }
    );

    try {
      await closeAnimation.finished;
    } catch {
      // The element may be removed while its close animation is running.
    }

    item.details.open = false;
    closeAnimation.cancel();
    item.details.removeAttribute('data-animate-close');
  }

  /**
   * Reads the longest CSS transition duration from an element.
   *
   * @param {Element} element - The element with the transition.
   * @returns {number} The transition duration in milliseconds.
   */
  #getTransitionDuration(element) {
    const style = window.getComputedStyle(element);
    const durations = style.transitionDuration.split(',').map((value) => this.#timeToMilliseconds(value.trim()));
    const delays = style.transitionDelay.split(',').map((value) => this.#timeToMilliseconds(value.trim()));

    return Math.max(...durations.map((duration, index) => duration + (delays[index % delays.length] ?? 0)), 0);
  }

  /**
   * Converts a CSS time value to milliseconds.
   *
   * @param {string} value - A CSS time value.
   * @returns {number} The time in milliseconds.
   */
  #timeToMilliseconds(value) {
    const amount = Number.parseFloat(value);
    return value.endsWith('ms') ? amount : amount * 1000;
  }

  /**
   * Closes the accordion when a click lands inside an element marked with
   * `[data-accordion-close]` (or one of its descendants). Used by markup that
   * provides an explicit close-target region inside the content area.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;

    if (!(/** @type {Element} */ (event.target).closest('[data-accordion-close]'))) return;

    this.summary.focus({ preventScroll: true });
    void this.#closeWithAnimation(this);
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    let shouldOpen =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));

    const group = this.closest('.accordion');
    if (shouldOpen && group?.hasAttribute('data-single-open')) {
      const items = this.#getGroupItems(group);
      const currentIndex = items.indexOf(this);
      shouldOpen = !items.slice(0, currentIndex).some((item) => item.details.open);
    }

    this.details.open = shouldOpen;
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.summary.focus();
      void this.#closeWithAnimation(this);
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
